import { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Form, Formik, FormikConfig, Field } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { TextField, Switch, ColorPicker } from 'common/components/FormField';
import { Button } from 'common/components/Button';
import { DescriptionButton } from 'common/components/DescriptionButton';
import { DescriptionBox } from 'common/components/DescriptionBox';
import {
    IBasicFileFragment,
    ICreateLibraryCollectionInput,
    IUpdateLibraryCollectionInput,
    ITrainingListItemFragment,
    ICollectionGroupFragment,
    IFileFragment,
} from 'graphql/types';
import { FormGroupSelector } from 'user/components/FormField/FormGroupSelector';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { TrainingSelector } from 'training/components/FormField/TrainingSelector';
import { FormSection } from 'common/components/FormSection';
import { Divider } from 'common/components/Divider';
import { LibraryHeaderImageField } from 'library/components/forms/LibraryHeaderImageField';
import { LibraryOverviewImageFormSection } from 'library/components/forms/LibraryOverviewImageFormSection';

type InputType = Omit<
    ICreateLibraryCollectionInput | IUpdateLibraryCollectionInput,
    'thumbnailId' | 'headerImageId'
> & {
    thumbnail?: IFileFragment | IBasicFileFragment | null;
    headerImage?: IFileFragment | IBasicFileFragment | null;
};

type FormValues = Omit<InputType, 'access'> & {
    accessTrainings: ITrainingListItemFragment[];
    accessGroups: ICollectionGroupFragment[];
};
interface IProps extends FormikConfig<FormValues> {
    isRootCollection?: boolean;
    disabled?: boolean;
    imageUploaded?: boolean;
    isDeleting?: boolean;
    onClose?(): void;
    onDelete?(): void;
}

export const LibraryCollectionForm = ({
    isRootCollection,
    disabled,
    imageUploaded,
    isDeleting,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [publishedDescriptionOpen, setPublishedDescriptionOpen] =
        useState(false);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(translate('validate.required')),
        description: Yup.string().optional(),
        active: Yup.boolean().default(false),
        titleColor: Yup.string().optional(),
    });

    return (
        <Formik validationSchema={validationSchema} {...other}>
            {({ submitForm, isSubmitting, values, dirty }) => (
                <>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={
                            (dirty || imageUploaded) &&
                            !isSubmitting &&
                            !isDeleting
                        }
                        onConfirm={submitForm}
                    />

                    <Form>
                        <LibraryHeaderImageField />

                        <Grid container spacing={2}>
                            <Grid size="grow">
                                <Field
                                    component={TextField}
                                    label={translate('title')}
                                    name="title"
                                />
                            </Grid>
                            <Grid>
                                <Field
                                    component={ColorPicker}
                                    name="titleColor"
                                    popoverPosition="bottom-end"
                                    tooltip={translate('titleColor')}
                                />
                            </Grid>
                        </Grid>
                        <Field
                            multiline
                            component={TextField}
                            label={translate('description')}
                            name="description"
                        />

                        <Divider sx={{ mt: 2 }} />

                        <LibraryOverviewImageFormSection />

                        {!isRootCollection && (
                            <>
                                <FormSection
                                    renderExpanded
                                    description={
                                        <Box>
                                            {translate(
                                                'collectionForm.trainingsSectionDescription'
                                            )}
                                        </Box>
                                    }
                                    title={translate(
                                        'collectionForm.trainingsSectionTitle'
                                    )}
                                >
                                    <Box sx={{ mt: 3 }}>
                                        <TrainingSelector
                                            name="accessTrainings"
                                            value={values.accessTrainings}
                                        />
                                    </Box>
                                </FormSection>

                                <FormSection
                                    renderExpanded
                                    description={
                                        <Box>
                                            {translate(
                                                'collectionForm.groupsSectionDescription'
                                            )}
                                        </Box>
                                    }
                                    title={translate(
                                        'collectionForm.accessForGroups'
                                    )}
                                >
                                    <Box sx={{ mt: 3 }}>
                                        <FormGroupSelector
                                            name="accessGroups"
                                            value={values.accessGroups}
                                        />
                                    </Box>
                                </FormSection>
                            </>
                        )}

                        {!isRootCollection && (
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ mb: 3 }}>
                                    <Grid
                                        container
                                        sx={{
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Grid
                                            sx={{
                                                mr: -1,

                                                '> .MuiFormControl-root': {
                                                    m: 0,
                                                },
                                            }}
                                        >
                                            <Field
                                                checked={values.active}
                                                component={Switch}
                                                label={translate('published')}
                                                name="active"
                                            />
                                        </Grid>

                                        <Grid>
                                            <DescriptionButton
                                                onClick={() =>
                                                    setPublishedDescriptionOpen(
                                                        !publishedDescriptionOpen
                                                    )
                                                }
                                            />
                                        </Grid>
                                    </Grid>

                                    <DescriptionBox
                                        open={publishedDescriptionOpen}
                                        onClose={() =>
                                            setPublishedDescriptionOpen(false)
                                        }
                                    >
                                        {translate(
                                            'libraryForm.publishedDescription'
                                        )}
                                    </DescriptionBox>
                                </Box>

                                <Divider />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <Button
                                color="primary"
                                disabled={
                                    isSubmitting ||
                                    disabled ||
                                    (!isSubmitting &&
                                        !disabled &&
                                        !dirty &&
                                        !imageUploaded)
                                }
                                loading={isSubmitting}
                                type="submit"
                                variant="contained"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    e.preventDefault();

                                    submitForm();
                                }}
                            >
                                {translate('save')}
                            </Button>

                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}

                            {!!onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </>
            )}
        </Formik>
    );
};
